import { c as _c } from "react/compiler-runtime";
import './payment-method-update-sheet.scss';
import { ReactElement, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Divider } from '@sola/ark-core/atoms/divider/divider.tsx';
import { FormSubmit, FormSubmitProps } from '@sola/ark-core/molecules/form/form.tsx';
import { Sheet, SheetActions, SheetBody, SheetClose, SheetContent, SheetFooter, SheetHeader, SheetTrigger } from '@sola/ark-core/molecules/sheet/sheet.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { usePaymentMethodUpdate } from '@sola/my-sdk/payment-methods/hooks.ts';
import { BillingFieldsEnum } from '@sola/schema/dotcom/checkout/billingInfo.ts';
import { AddressSelectOptions, BillingAddressEnum } from '@sola/schema/dotcom/shared/address.ts';
import { SupporterErrorTypeEnum } from '@sola/schema/dotcom/supporters/errors.ts';
import { SupporterAchFieldsEnum, SupporterCCFieldsEnum } from '@sola/schema/dotcom/supporters/paymentMethods.ts';
import { GraphQLCommitment, GraphQLPaymentMethodBillingAddress, GraphQLSupporterPaymentMethod } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getLongExpiryDate } from '@sola/shared-utils/credit-cards.ts';
import { userErrorPaymentMethodUpdate } from '@sola/shared-utils/errors/supporters.ts';
import { getStreet } from '@sola/shared-utils/supporters/addresses.ts';
import { useSupporterBillingAddresses } from '@sola/supporter-sdk/hooks/payment-methods.ts';
import { getNonRecurringCommitments } from '@sola/supporter-sdk/utils/commitments.ts';
import { useLocalAlerts } from '../../molecules/local-alerts/local-alerts-provider.tsx';
import { LocalAlert } from '../../molecules/local-alerts/local-alerts.tsx';
import useModalAlerts from '../../molecules/local-alerts/useModalAlerts.ts';
import { PaymentMethodUpdateForm } from '../../molecules/payment-method-update-form/payment-method-update-form.tsx';
import { createPaymentMethodPartialSuccessAlert, createPaymentMethodSuccessAlert, createSupportAlert } from '../../utils/alerts.tsx';
import { getErrorTranslationKeys } from '../../utils/errors.ts';
import { RecurringGivingRadioOptionsEnum } from '../recurring-giving-options/recurring-giving-options.tsx';
type PaymentMethodUpdateSheetFooterProps = Pick<FormSubmitProps, 'isBusy'>;
const PaymentMethodUpdateSheetFooter: React.FC<PaymentMethodUpdateSheetFooterProps> = t0 => {
  const $ = _c(13);
  const {
    isBusy
  } = t0;
  const {
    t
  } = useTranslation("core");
  let t1;
  if ($[0] !== t) {
    t1 = t("core:actions.cancel");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <SheetClose asChild={true}><ButtonStyle size="lg" purpose="neutral" variant="secondary" fullWidth={true}><button type="button">{t1}</button></ButtonStyle></SheetClose>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArkIcon iconName="lock-01" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("core:actions.update");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isBusy || $[8] !== t4) {
    t5 = <FormSubmit iconBefore={t3} isBusy={isBusy} disableUntilFilled={true}>{t4}</FormSubmit>;
    $[7] = isBusy;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t2 || $[11] !== t5) {
    t6 = <SheetFooter><SheetActions>{t2}{t5}</SheetActions></SheetFooter>;
    $[10] = t2;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
export interface PaymentMethodUpdateProps {
  openTrigger: ReactElement;
  paymentMethod: GraphQLSupporterPaymentMethod;
  commitments: GraphQLCommitment[];
}
const useComponentData = ({
  paymentMethod,
  commitments
}: Pick<PaymentMethodUpdateProps, 'paymentMethod' | 'commitments'>) => {
  const {
    t
  } = useTranslation('my');
  const [isOpen, setIsOpen] = useState(false);
  const {
    addAlerts,
    clearAlerts
  } = useLocalAlerts();
  const {
    alerts: sheetAlerts,
    setAlerts: setSheetAlerts,
    clearAlerts: clearSheetAlerts,
    removeAlert: removeSheetAlert,
    handleOpenChange
  } = useModalAlerts();
  const updatePaymentMethod = usePaymentMethodUpdate();
  const {
    supporterAddresses
  } = useSupporterBillingAddresses();
  const nonRecurringCommitments = getNonRecurringCommitments(commitments);
  const updateAch = (data: FieldValues): GraphQLSupporterPaymentMethod | undefined => {
    if (!paymentMethod.ach) {
      return;
    }
    return {
      ...paymentMethod,
      accountNickname: data[SupporterAchFieldsEnum.nickname],
      ach: {
        ...paymentMethod.ach,
        accountName: data[SupporterAchFieldsEnum.accountName]
      }
    };
  };
  const updateCard = (data_0: FieldValues): GraphQLSupporterPaymentMethod | undefined => {
    if (!paymentMethod.creditCard) {
      return;
    }
    let billingAddress: GraphQLPaymentMethodBillingAddress;
    if (data_0[BillingFieldsEnum.AddressOption] === AddressSelectOptions.Add) {
      billingAddress = {
        street: data_0[BillingAddressEnum.Address],
        city: data_0[BillingAddressEnum.City],
        stateCode: data_0[BillingAddressEnum.State],
        postalCode: data_0[BillingAddressEnum.Postal],
        country: data_0[BillingAddressEnum.Country]
      };
    } else {
      // NEW CREDIT CARD WITHOUT NEW BILLING ADDRESS
      const selectedAddress = supporterAddresses?.get(data_0[BillingFieldsEnum.AddressOption]);
      if (!selectedAddress) {
        return;
      }
      const {
        billingAddress: billingAddress1,
        billingAddress2,
        billingCity: city,
        billingState: stateCode,
        billingPostal: postalCode,
        billingCountry: country
      } = selectedAddress.billingAddress;
      billingAddress = {
        street: getStreet(billingAddress1, billingAddress2),
        city,
        stateCode,
        postalCode,
        country
      };
    }
    const expirationDate = getLongExpiryDate(data_0[SupporterCCFieldsEnum.expirationDate]);
    return {
      ...paymentMethod,
      accountNickname: data_0[SupporterCCFieldsEnum.nickname],
      creditCard: {
        ...paymentMethod.creditCard,
        cardholderName: data_0[SupporterCCFieldsEnum.nameOnCard],
        expirationDate
      },
      billingAddress
    };
  };
  const handleSubmit = async (data_1: FieldValues) => {
    clearSheetAlerts();
    clearAlerts();
    try {
      const updatedPaymentMethod = paymentMethod.ach ? updateAch(data_1) : updateCard(data_1);
      if (!updatedPaymentMethod) {
        throw userErrorPaymentMethodUpdate();
      }
      let commitmentIds = [] as string[];
      if (data_1.recurringGiving === RecurringGivingRadioOptionsEnum.Yes) {
        commitmentIds = nonRecurringCommitments?.map(commitment => commitment.internalId).filter((id): id is string => typeof id === 'string') ?? [];
      }
      const {
        paymentMethod: savedPaymentMethod,
        isPartialSuccess
      } = await updatePaymentMethod.mutateAsync({
        paymentMethod: updatedPaymentMethod,
        commitmentIds
      });
      const alerts = [createPaymentMethodSuccessAlert(savedPaymentMethod, 'updatePaymentMethod.success.action')];
      if (isPartialSuccess) {
        alerts.push(createPaymentMethodPartialSuccessAlert());
      }
      addAlerts(alerts);
      handleOpenChange(false, setIsOpen);
    } catch (errors) {
      const translationKeys = getErrorTranslationKeys(SupporterErrorTypeEnum.PaymentMethodUpdate, errors);
      setSheetAlerts(translationKeys.map(translationKey => {
        return createSupportAlert(translationKey);
      }));
    }
  };
  return {
    t,
    isOpen,
    handleOpenChange,
    setIsOpen,
    sheetAlerts,
    removeSheetAlert,
    nonRecurringCommitments,
    handleSubmit,
    isUpdatePending: updatePaymentMethod.isPending
  };
};
export const PaymentMethodUpdateSheet: React.FC<PaymentMethodUpdateProps> = t0 => {
  const $ = _c(42);
  const {
    openTrigger,
    paymentMethod,
    commitments
  } = t0;
  let t1;
  if ($[0] !== commitments || $[1] !== paymentMethod) {
    t1 = {
      paymentMethod,
      commitments
    };
    $[0] = commitments;
    $[1] = paymentMethod;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    t,
    isOpen,
    handleOpenChange,
    setIsOpen,
    sheetAlerts,
    removeSheetAlert,
    nonRecurringCommitments,
    handleSubmit,
    isUpdatePending
  } = useComponentData(t1);
  let t2;
  if ($[3] !== handleOpenChange || $[4] !== setIsOpen) {
    t2 = open => handleOpenChange(open, setIsOpen);
    $[3] = handleOpenChange;
    $[4] = setIsOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== openTrigger) {
    t3 = <SheetTrigger>{openTrigger}</SheetTrigger>;
    $[6] = openTrigger;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t) {
    t4 = t("updatePaymentMethod.form.title");
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("updatePaymentMethod.form.title");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <SheetHeader>{t5}</SheetHeader>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("updatePaymentMethod.form.leadin");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <p className="body-sm md:body-md body-neutral-mild">{t7}</p>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== removeSheetAlert || $[19] !== sheetAlerts) {
    t9 = sheetAlerts.length > 0 && <div className="mt-comfy-sm md:mt-comfy-lg">{sheetAlerts.map(sheetAlert => <LocalAlert key={sheetAlert.id} alert={sheetAlert} className="my-comfy-xs" onClose={removeSheetAlert} />)}</div>;
    $[18] = removeSheetAlert;
    $[19] = sheetAlerts;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Divider purpose="neutral" className="mt-comfy-lg mb-comfy-xl md:mt-comfy-xl" />;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== isUpdatePending) {
    t11 = <PaymentMethodUpdateSheetFooter isBusy={isUpdatePending} />;
    $[22] = isUpdatePending;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== handleSubmit || $[25] !== nonRecurringCommitments || $[26] !== paymentMethod || $[27] !== t11) {
    t12 = <PaymentMethodUpdateForm paymentMethod={paymentMethod} nonRecurringCommitments={nonRecurringCommitments} onSubmit={handleSubmit}>{t11}</PaymentMethodUpdateForm>;
    $[24] = handleSubmit;
    $[25] = nonRecurringCommitments;
    $[26] = paymentMethod;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t8 || $[31] !== t9) {
    t13 = <SheetBody>{t8}{t9}{t10}{t12}</SheetBody>;
    $[29] = t12;
    $[30] = t8;
    $[31] = t9;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t13 || $[34] !== t4 || $[35] !== t6) {
    t14 = <SheetContent title={t4}>{t6}{t13}</SheetContent>;
    $[33] = t13;
    $[34] = t4;
    $[35] = t6;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== isOpen || $[38] !== t14 || $[39] !== t2 || $[40] !== t3) {
    t15 = <Sheet open={isOpen} onOpenChange={t2}>{t3}{t14}</Sheet>;
    $[37] = isOpen;
    $[38] = t14;
    $[39] = t2;
    $[40] = t3;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  return t15;
};